import { useEffect, useRef } from 'react'

/**
 * Nachsehen, wenn die App zurückkommt.
 *
 * **Warum das gebraucht wird:** Auf dem Telefon wird eine installierte App so gut wie
 * nie neu gestartet. Sie wird weggelegt und wieder hervorgeholt — der Code läuft die
 * ganze Zeit weiter, und alles, was beim Start einmal geprüft wurde, bleibt auf dem
 * Stand von damals. Ein Gerät, das seit Tagen in der Tasche liegt, zeigte ohne diese
 * Prüfung tagealte Angaben, ohne dass irgendetwas kaputt wäre.
 *
 * Drei Gelegenheiten, bei denen es etwas Neues zu erfahren geben kann:
 *
 * - **Die App wird wieder sichtbar** (`visibilitychange`). Der Normalfall: App
 *   gewechselt, Telefon entsperrt, aus der Meldung heraus geöffnet.
 * - **Die Seite kommt aus dem Zwischenspeicher zurück** (`pageshow` mit `persisted`).
 *   Der Weg, den Safari beim Zurückblättern nimmt; dabei feuert nicht überall ein
 *   `visibilitychange`.
 * - **Das Netz ist wieder da** (`online`). Für eine App, die im Funkloch arbeitet, die
 *   eigentliche Gelegenheit — vorher konnte die Prüfung gar nicht gelingen.
 *
 * Geprüft wird nur nach einer **echten Abwesenheit**: Ein `visibilitychange`, dem kein
 * `hidden` vorausging, wird übergangen. Sonst löste jedes Ereignis, das der Browser
 * nebenbei schickt, eine Anfrage aus.
 */

/**
 * Kürzester Abstand zwischen zwei Prüfungen.
 *
 * Wer zwischen der App und dem Funkgerät hin- und herwechselt, soll nicht bei jedem
 * Wechsel eine Anfrage auslösen. Eine Minute ist der Ausgleich: kurz genug, dass eine
 * Rückkehr sich wie „frisch nachgesehen“ anfühlt, lang genug, dass schnelles Wechseln
 * nichts kostet. Die erste Rückkehr nach dem Start wird immer geprüft — sie kann Stunden
 * nach dem Start kommen.
 */
const MINDESTABSTAND_MS = 60_000

export function useRueckkehr(pruefen: () => void): void {
  const letztePruefung = useRef(0)

  // Der Aufrufer darf bei jedem Rendern eine neue Funktion übergeben; die Ereignisse
  // sollen davon unberührt bleiben und genau einmal angemeldet werden. Deshalb liegt
  // der Rückruf in einer Referenz, die der Effekt unten nie als Abhängigkeit sieht.
  const aktuell = useRef(pruefen)
  useEffect(() => {
    aktuell.current = pruefen
  }, [pruefen])

  useEffect(() => {
    let warVersteckt = false

    const vielleichtPruefen = () => {
      const jetzt = Date.now()
      if (jetzt - letztePruefung.current < MINDESTABSTAND_MS) return
      letztePruefung.current = jetzt
      aktuell.current()
    }

    const beiSichtbarkeit = () => {
      if (document.visibilityState === 'hidden') {
        warVersteckt = true
        return
      }
      if (!warVersteckt) return
      warVersteckt = false
      vielleichtPruefen()
    }

    const beiRueckblaettern = (event: PageTransitionEvent) => {
      // Nur aus dem Zwischenspeicher. Ein gewöhnliches `pageshow` gehört zum Laden der
      // Seite — dort hat der Start bereits alles geprüft.
      if (event.persisted) vielleichtPruefen()
    }

    document.addEventListener('visibilitychange', beiSichtbarkeit)
    window.addEventListener('pageshow', beiRueckblaettern)
    window.addEventListener('online', vielleichtPruefen)

    return () => {
      document.removeEventListener('visibilitychange', beiSichtbarkeit)
      window.removeEventListener('pageshow', beiRueckblaettern)
      window.removeEventListener('online', vielleichtPruefen)
    }
  }, [])
}

/**
 * Den Browser nach einer neuen Fassung der App sehen lassen.
 *
 * Von sich aus sucht er nur bei einer Navigation danach — die in einer installierten
 * App über Tage nicht vorkommt. Findet er eine, meldet sich der Update-Hinweis über
 * denselben Weg wie sonst; hier wird nur angestoßen, nicht entschieden.
 *
 * Die Anfrage ist billig: Der Browser fragt mit `If-Modified-Since` nach und bekommt in
 * aller Regel ein 304 ohne Rumpf zurück.
 */
export async function sucheNeueFassung(): Promise<void> {
  if (!('serviceWorker' in navigator)) return

  try {
    const registrierung = await navigator.serviceWorker.getRegistration()
    await registrierung?.update()
  } catch {
    // Kein Netz oder noch kein Worker registriert. Beim nächsten Mal wieder.
  }
}
